import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Flex } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, TitleText } from 'styling/Typography'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { RootState } from 'store'
import useQueryPagination from 'hooks/useQueryPagination'
import { getDishes } from 'features/food/foodSlice'
import {
  FoodDishSourceType,
  IKitchenDishResponse,
} from 'types/api/foodApi.interface'
import { DishesModal } from 'pages/Food/DishesModal'

interface IProps {
  createOpen: boolean
  setCreateOpen: (open: boolean) => void
}

export const DishesTab = ({ createOpen, setCreateOpen }: IProps) => {
  const { dishes, isLoading } = useSelector((store: RootState) => store.food)
  const [editId, setEditId] = useState<number | null>(null)

  const params = useMemo(() => ({ page: 1, limit: 100 }), [])
  useQueryPagination({
    fetchFunctions: [getDishes],
    cleanUpFunctions: [],
    params,
  })

  const columns: IHonestTableColumn<IKitchenDishResponse>[] = [
    {
      id: 'name',
      header: 'Dish',
      sticky: true,
      minWidth: '14rem',
      cell: (d) => (
        <Cell>
          <BodyText weight='600'>{d.name}</BodyText>
          {d.supplier && <BodyText grey>Supplier: {d.supplier.name}</BodyText>}
        </Cell>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      cell: (d) => (
        <StatusPill
          status={
            d.sourceType === FoodDishSourceType.Prepared
              ? 'info'
              : d.sourceType === FoodDishSourceType.Purchased
                ? 'success'
                : 'accent'
          }
        >
          {d.sourceType === FoodDishSourceType.Prepared
            ? 'Prepared'
            : d.sourceType === FoodDishSourceType.Purchased
              ? 'Purchased'
              : `Type ${d.sourceType}`}
        </StatusPill>
      ),
    },
    {
      id: 'tags',
      header: 'Dietary tags',
      cell: (d) => (
        <Flex wrap gap='0.25rem'>
          {(d.dietaryTags || []).map((t) => (
            <StatusPill key={t.code || t.id} status='accent'>
              {t.code || t.name}
            </StatusPill>
          ))}
          {!d.dietaryTags?.length && <BodyText grey>—</BodyText>}
        </Flex>
      ),
    },
    {
      id: 'sku',
      header: 'Dish SKU',
      cell: (d) => <BodyText>{d.dishSku?.sku || d.dishSku?.name || '—'}</BodyText>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (d) => (
        <StatusPill status={d.active ? 'success' : 'accent'}>
          {d.active ? 'Active' : 'Inactive'}
        </StatusPill>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (d) => (
        <Flex justend width='100%'>
          <Button variant='secondary' size='sm' onClick={() => setEditId(d.id)}>
            Edit
          </Button>
        </Flex>
      ),
    },
  ]

  const closeModal = () => {
    setCreateOpen(false)
    setEditId(null)
  }

  return (
    <Flex column gap='1rem' width='100%'>
      <Container>
        <ContainerHeader>
          <TitleText weight='700'>Dishes</TitleText>
        </ContainerHeader>
        <ContainerContent>
          <HonestTable
            columns={columns}
            data={dishes.data || []}
            keyField='id'
            loading={isLoading}
            emptyMessage='No dishes yet'
          />
        </ContainerContent>
      </Container>
      {(createOpen || editId != null) && (
        <DishesModal closeModal={closeModal} id={editId ?? undefined} />
      )}
    </Flex>
  )
}
